import React from "react";
import {
  evaluateInformation,
  informationLabel,
  parseInformationTime,
  sanitizeInformationState,
  type FailureCode,
  type InformationBase,
  type InformationState,
  type InformationTime,
} from "@shared/informationState";

const FAILURE_TEXT: Record<FailureCode, string> = {
  "missing-configuration": "Provider configuration missing",
  authentication: "Provider authentication failed",
  entitlement: "Provider access unavailable for this plan",
  "rate-limit": "Provider rate limit reached",
  "local-budget-cutover": "Free allowance startup cooldown; no provider request sent",
  "local-budget-exhausted": "Local free allowance exhausted; no provider request sent",
  "local-budget-unavailable": "Local request budget unavailable; no provider request sent",
  http: "Provider HTTP request failed",
  "malformed-response": "Provider response failed validation",
  timeout: "Provider request timed out",
  network: "Provider network request failed",
  unknown: "Provider request failed",
};

export function formatInformationTime(time: InformationTime | string | null | undefined): string {
  const parsed = typeof time === "string" ? parseInformationTime(time) : time;
  if (!parsed) return "Unknown";
  if (parsed.precision === "date") return parsed.value;
  if (parsed.precision === "local") return `${parsed.value} (${parsed.timeZone ?? "time zone unknown"})`;
  return parsed.value.replace("T", " ").replace(/Z$/, " UTC");
}

export function displayInformation(information?: InformationState | null, captured = false): InformationState | undefined {
  const safe = sanitizeInformationState(information);
  return safe ? captured ? safe : evaluateInformation(safe, Date.now()) : undefined;
}

export function informationDisplayLabel(information?: InformationState | null, captured = false): string {
  const state = displayInformation(information, captured);
  return state ? informationLabel(state) : "Information state unknown";
}

const CLOCK_LABELS = [
  ["observedAt", "Observed"],
  ["publishedAt", "Published"],
  ["updatedAt", "Provider updated"],
  ["providerResponseAt", "Provider response"],
  ["providerSeenAt", "Provider seen"],
  ["effectiveAt", "Effective"],
  ["retrievedAt", "TSM retrieved"],
  ["lastSuccessfulRetrievalAt", "Last successful retrieval"],
  ["lastAttemptAt", "Last retrieval attempt"],
  ["cacheInsertedAt", "Cache inserted"],
  ["nextExpectedReleaseAt", "Next expected release"],
  ["validUntil", "Provider validity ends"],
  ["cacheRetainUntil", "Cache retention ends"],
  ["vintageStart", "Vintage starts"],
  ["vintageEnd", "Vintage ends"],
] as const;

function InformationDetails({ state }: { state: InformationBase }) {
  const clocks = CLOCK_LABELS.filter(([key]) => state.clocks[key] != null);
  const failure = state.provider.failure;
  const localDecision = failure?.code.startsWith("local-budget-");
  const accessDecision = failure && ["entitlement", "missing-configuration"].includes(failure.code)
    && (failure.requestSent === false || state.clocks.lastAttemptAt === null);
  return <>
    <div style={{ display: "block" }}>Provider: {state.provider.health} · Activity: {state.provider.activity} · Coverage: {state.data.availability} · Validity: {state.data.validity}</div>
    {failure && <div style={{ display: "block" }}>{FAILURE_TEXT[failure.code] ?? FAILURE_TEXT.unknown} · {localDecision ? "Request deferred" : accessDecision ? "Access decision" : "Failed attempt"}: {formatInformationTime(failure.occurredAt)}{failure.retryAt ? ` · Retry after: ${formatInformationTime(failure.retryAt)}` : ""}</div>}
    {clocks.length === 0 && <div style={{ display: "block" }}>Provider dates and retrieval time: Unknown</div>}
    {clocks.map(([key, label]) => <div key={key} style={{ display: "block" }}>{label}: {formatInformationTime(state.clocks[key])}</div>)}
    {state.clocks.vintage && <div style={{ display: "block" }}>Vintage: {state.clocks.vintage}</div>}
    {["open", "closed"].includes(state.session.state) && <>
      <div style={{ display: "block" }}>Session context: {state.session.state} · Basis: {state.session.basis}{state.session.sessionDate ? ` · Session date: ${state.session.sessionDate}` : ""}{state.session.timeZone ? ` · Time zone: ${state.session.timeZone}` : ""}</div>
      {state.session.latestSessionStartAt && <div style={{ display: "block" }}>Latest session starts: {formatInformationTime(state.session.latestSessionStartAt)}</div>}
      {state.session.latestSessionEndAt && <div style={{ display: "block" }}>Latest session ends: {formatInformationTime(state.session.latestSessionEndAt)}</div>}
    </>}
  </>;
}

/** Captured report/history evidence retains its state at capture; current feeds are reevaluated. */
export default function InformationDisplay({ information, captured = false, expandable = true }: { information?: InformationState | null; captured?: boolean; expandable?: boolean }) {
  const state = displayInformation(information, captured);
  const components = state?.components?.map((component) => <div key={component.id} style={{ display: "block", borderTop: "1px solid #333", marginTop: 5, paddingTop: 5 }}>
    <div style={{ fontWeight: 700, display: "block" }}>{component.instrument ?? component.id}{component.venue ? ` · ${component.venue}` : ""}{component.value != null && component.data.validity === "valid" && ["available", "partial"].includes(component.data.availability) ? ` · ${component.value}` : ""}{component.unit ? ` ${component.unit}` : ""}{component.currency ? ` · ${component.currency}` : ""}</div>
    <div style={{ display: "block" }}>{informationLabel(component)}</div>
    <InformationDetails state={component} />
  </div>);
  return <div className="information-display" style={{ fontSize: 11, lineHeight: 1.5, color: "#aaa", marginTop: 6, display: "block", overflowWrap: "anywhere", minWidth: 0 }}>
    <div style={{ fontWeight: 700, display: "block" }}>{state ? informationLabel(state) : "Information state unknown"}</div>
    {state ? <>
      <InformationDetails state={state} />
      {!!state.components?.length && (expandable ? <details style={{ marginTop: 4 }} onClick={(event) => event.stopPropagation()}>
        <summary>Component values and dates ({state.components.length})</summary>
        {components}
      </details> : <div style={{ display: "block", marginTop: 4 }}>Component values and dates ({state.components.length}){components}</div>)}
    </> : <div style={{ display: "block" }}>Provider dates and retrieval time: Unknown</div>}
  </div>;
}
